import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";

const API = `/api`;

const fallbackQuotes = [
  { id: "1", name: "Ana Rodrigues", club: "Maia", rating: 5, text: "O ambiente é incrível e os instrutores são super motivadores! Perdi 12kg em 6 meses e nunca me senti tão bem.", image_url: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=60&w=100&auto=format&fit=crop" },
  { id: "2", name: "Miguel Ferreira", club: "Barcelos", rating: 5, text: "Qualidade premium a um preço justo. As aulas de grupo são fantásticas e há sempre muita variedade.", image_url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=60&w=100&auto=format&fit=crop" },
  { id: "3", name: "Sofia Almeida", club: "Maia", rating: 5, text: "O personal training mudou a minha vida. Finalmente consegui atingir os meus objetivos com acompanhamento profissional.", image_url: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=60&w=100&auto=format&fit=crop" },
];

export const Testimonials = () => {
  const [quotes, setQuotes] = useState(fallbackQuotes);
  const [quoteIndex, setQuoteIndex] = useState(0);

  useEffect(() => {
    axios.get(`${API}/content/testimonials/quotes`)
      .then((res) => {
        if (res.data?.length > 0) setQuotes(res.data);
      })
      .catch(() => {});
  }, []);

  const visibleQuotes = quotes.slice(quoteIndex, quoteIndex + 3);
  const canPrev = quoteIndex > 0;
  const canNext = quoteIndex + 3 < quotes.length;

  return (
    <section id="testemunhos" className="py-20 lg:py-28 bg-[#FAF8F5]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="text-[#FED601] text-sm font-bold uppercase tracking-widest">
            Histórias de Sucesso
          </span>
          <h2 className="text-4xl lg:text-5xl font-black text-[#1A1A1A] mt-2 mb-4">
            O Que Dizem <span className="text-[#FED601]">de Nós</span>
          </h2>
        </motion.div>

        <div className="relative">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(visibleQuotes.length > 0 ? visibleQuotes : quotes.slice(0, 3)).map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-[#E0DDD7] rounded-xl p-6 hover:shadow-lg hover:border-[#FED601]/30 transition-all duration-300"
                data-testid={`testimonial-card-${testimonial.id}`}
              >
                <Quote size={28} className="text-[#FED601]/30 mb-4" />
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(parseInt(testimonial.rating) || 5)].map((_, i) => (
                    <Star key={i} size={14} className="text-[#FED601] fill-[#FED601]" />
                  ))}
                </div>
                <p className="text-[#4A4A4A] leading-relaxed mb-5 text-sm">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-[#E0DDD7]">
                  {testimonial.image_url && (
                    <img
                      src={testimonial.image_url}
                      alt={testimonial.name}
                      className="w-10 h-10 rounded-full object-cover"
                      loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div>
                    <p className="text-[#1A1A1A] font-bold text-sm">{testimonial.name}</p>
                    <p className="text-[#FED601] text-xs">Clube de {testimonial.club}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {quotes.length > 3 && (
            <div className="flex justify-center gap-3 mt-8">
              <button
                onClick={() => setQuoteIndex(Math.max(0, quoteIndex - 3))}
                disabled={!canPrev}
                className="w-10 h-10 rounded-full border border-[#E0DDD7] flex items-center justify-center text-[#4A4A4A] hover:border-[#FED601] hover:text-[#FED601] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Testemunhos anteriores"
                data-testid="quotes-prev-btn"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => setQuoteIndex(Math.min(quotes.length - 1, quoteIndex + 3))}
                disabled={!canNext}
                className="w-10 h-10 rounded-full border border-[#E0DDD7] flex items-center justify-center text-[#4A4A4A] hover:border-[#FED601] hover:text-[#FED601] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Testemunhos seguintes"
                data-testid="quotes-next-btn"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 bg-[#1A1A1A] rounded-xl p-8"
        >
          <div className="text-center">
            <span className="stat-number text-3xl lg:text-4xl">4.7/5</span>
            <p className="text-gray-400 text-sm uppercase tracking-wider mt-1">Avaliação Google</p>
          </div>
          <div className="text-center">
            <span className="stat-number text-3xl lg:text-4xl">98%</span>
            <p className="text-gray-400 text-sm uppercase tracking-wider mt-1">Satisfação</p>
          </div>
          <div className="text-center">
            <span className="stat-number text-3xl lg:text-4xl">9+</span>
            <p className="text-gray-400 text-sm uppercase tracking-wider mt-1">Anos Experiência</p>
          </div>
          <div className="text-center">
            <span className="stat-number text-3xl lg:text-4xl">50+</span>
            <p className="text-gray-400 text-sm uppercase tracking-wider mt-1">Profissionais</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
